import { handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import {
  Actions,
  Subjects,
  SystemRoles,
  User,
  UserCompanyData,
} from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { UserPropertiesForm } from '@/components/users/forms/UserPropertiesForm';
import { useAzureADHandler } from '@/hooks/integrations/useAzureAD.hook';
import { useUpdateUser, useUser } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { queryStateConverter } from '@/utils/helpers';
import { PostHogFeatureFlags } from '@/utils/posthog';
import { AxiosError } from 'axios';
import { MenuItem } from 'primereact/menuitem';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { LoadingPage } from '../LoadingPage';
import { UserTabs } from './UserTabs';

const getBreadcrumbs = (
  account: Account,
  user: User,
  t: TranslationFunctionType,
): MenuItem[] => {
  const items = [
    {
      label: account?.name,
      url: account.isSystem
        ? RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id)
        : RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('users'),
      url: RedirectPaths[RedirectPathsEnum.USERS](),
      template: AppBreadCrumbTemplate,
    },
    {
      label: user?.name,
      className: 'active',
      url: RedirectPaths[RedirectPathsEnum.USERS_EDIT](user?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('generic.properties'),
      url: RedirectPaths[RedirectPathsEnum.USERS_PROPERTIES](user.id),
      template: AppBreadCrumbTemplate,
    },
  ];

  return items;
};

export const UserPropertiesPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { cannot } = usePermission();
  const { canUseFeature } = useFeatureFlag();
  const isManagerUserReferenceEnabled = canUseFeature(
    PostHogFeatureFlags.MANAGER_USER_REFERENCE,
  );
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const toast = useToast();
  const { user, isLoading } = useUser({
    userId: id,
    accountId: currentAccount?.id,
  });

  let initialValues: UserCompanyData & {
    avatar?: string;
    hubspotMeetingLink?: string;
  } = {};

  if (user && !isLoading) {
    const companyData = user.companyData;

    initialValues = {
      companyName: companyData?.companyName,
      department: companyData?.department,
      manager: companyData?.manager,
      managerEmail: companyData?.managerEmail,
      managerUser: companyData?.managerUser ?? null,
      country: companyData?.country,
      jobTitle: companyData?.jobTitle,
      mobilePhone: companyData?.mobilePhone,
      officeLocation: companyData?.officeLocation,
      avatar: user?.avatar,
      hubspotMeetingLink: user?.meta?.hubspotMeetingLink,
    };
  }

  const updateUser = useUpdateUser();

  const handleUpdateUserProps = async (
    data: UserCompanyData & { avatar?: string; hubspotMeetingLink?: string },
  ) => {
    const hasManagerUserChanged =
      data.managerUser?.id !== initialValues.managerUser?.id;

    try {
      await updateUser.update({
        userId: id as string,
        accountId: (currentAccount as Account)?.id,
        updates: {
          companyName: data.companyName,
          department: data.department,
          ...(isManagerUserReferenceEnabled
            ? hasManagerUserChanged
              ? { managerUserId: data.managerUser?.id ?? '' }
              : {}
            : { manager: data.manager, managerEmail: data.managerEmail }),
          country: data.country,
          jobTitle: data.jobTitle,
          mobilePhone: data.mobilePhone,
          officeLocation: data.officeLocation,
          avatar: currentAccount?.isSystem ? data.avatar : undefined,
          hubspotMeetingLink: data.hubspotMeetingLink,
        },
      });
      toast?.success(t('toast.success'), t('user.updated'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const isReadOnly =
    cannot(Actions.UPDATE, Subjects.USERS) ||
    (currentAccount?.isSystem &&
      currentUser?.role.code !== SystemRoles.DEVELOPER);

  // TODO: In near future this should include Google Workspace fields as well
  const [syncedAdFields, setSyncedAdFields] = useState<string[]>([]);
  const { accountAd, isADLoading } = useAzureADHandler(
    currentAccount?.id as string,
  );

  useEffect(() => {
    if (accountAd && accountAd.active) {
      setSyncedAdFields(
        accountAd.attributesMap
          ?.filter((attribute) => attribute.synced)
          .map((attribute) => attribute.entityField ?? '') ?? [],
      );
    }
  }, [accountAd]);

  if (!user || isLoading || isADLoading) {
    return <LoadingPage message={t('user.loading')} />;
  }

  return (
    <>
      {currentAccount && user && (
        <>
          <AppBreadCrumb model={getBreadcrumbs(currentAccount, user, t)} />
          <h1>{user.name ? user.name : user.email}</h1>
          <UserTabs user={user} />
        </>
      )}

      <UserPropertiesForm
        initialValues={initialValues}
        onSubmit={handleUpdateUserProps}
        state={queryStateConverter(updateUser)}
        isDisabled={isReadOnly}
        isSuperAdmin={user?.role?.code === SystemRoles.SUPER_ADMIN}
        syncedAdFields={syncedAdFields}
        isAdSyncedField={accountAd?.active}
        isManagerSyncedField={accountAd?.managerFieldSynced ?? false}
      />
    </>
  );
};
